import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Beaker, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { isAtLeastSolo } from '@/lib/role';
import { errorHandler, cn } from '@/lib/utils';
import { fetchCampaign } from '@/services/campaign';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FeatureLocked } from '@/components/feature-locked';
import { FullScreenLoader } from '@/components/full-screen-loader';
import { HeroKpi } from '@/components/hero-kpi';
import { InsightsPanel } from '@/components/insights-panel';
import { computeThemeExperimentInsight } from '@/helpers/analytics-insights.helper';
import { paletteFor } from '@/helpers/breakdown-palette.helper';
import { useActiveDesignExperiment, useConcludeDesignExperiment, useDesignExperimentResults, } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout/themes/-features/use-design-experiments';
import { StartExperimentDialog } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout/themes/-features/start-experiment-dialog';
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/ab-testing/_layout/themes/')({
    component: ThemeABPage,
    loader: ({ params: { publicId } }) => fetchCampaign(publicId),
});
function ThemeABPage() {
    const { publicId } = Route.useParams();
    const campaign = Route.useLoaderData();
    const { workspace } = useAuthenticationState();
    const { from, to, excludeDevices, excludeBrowsers } = Route.useSearch();
    const { t } = useTranslation('campaign');
    const [fromDate, toDate] = React.useMemo(() => [new Date(from), new Date(to)], [from, to]);
    const canUse = workspace && isAtLeastSolo(workspace);
    const { data: active, isPending: activePending } = useActiveDesignExperiment(publicId);
    const { data: results, isPending: resultsPending } = useDesignExperimentResults({
        campaignPublicId: publicId,
        experimentPublicId: active?.publicId ?? null,
        from: fromDate,
        to: toDate,
        excludeDevices,
        excludeBrowsers,
        enabled: !!active,
    });
    const { mutate: conclude, isPending: concluding } = useConcludeDesignExperiment(publicId);
    const handleConclude = React.useCallback((winnerKey) => {
        if (!active)
            return;
        const confirmed = window.confirm(t('design_experiment.confirm_conclude', {
            key: winnerKey,
            defaultValue: `Promote variant ${winnerKey} to the campaign and end this experiment?`,
        }));
        if (!confirmed)
            return;
        conclude({ campaignPublicId: publicId, experimentPublicId: active.publicId, winnerKey }, {
            onSuccess: () => toast.success(t('design_experiment.concluded', { defaultValue: 'Winner promoted to the campaign' })),
            onError: errorHandler,
        });
    }, [active, conclude, publicId, t]);
    if (!canUse) {
        return (<FeatureLocked title={t('design_experiment.locked_title', { defaultValue: 'Theme A/B Testing' })} description={t('design_experiment.locked_description', {
                defaultValue: 'Run a sticky-routed A/B test across themes and patterns to find the winning design.',
            })} plan="Solo+"/>);
    }
    if (activePending) {
        return <FullScreenLoader />;
    }
    if (!active) {
        return (<Card className="border-dashed bg-muted/10">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Beaker className="h-5 w-5"/>
                        {t('design_experiment.empty_title', { defaultValue: 'No active theme experiment' })}
                    </CardTitle>
                    <CardDescription>
                        {t('design_experiment.empty_description', {
                defaultValue: 'Test two or more themes against each other. Visitors are routed sticky-by-fingerprint so they always see the same variant; click-through rate per variant tells you which one wins.',
            })}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <StartExperimentDialog campaignPublicId={publicId} seedVariant={{
                pageDesign: campaign.pageDesign ?? null,
                backgroundPattern: campaign.backgroundPattern ?? null,
            }}>
                        <Button>
                            <Beaker className="h-4 w-4 mr-2"/>
                            {t('design_experiment.start_cta', { defaultValue: 'Start experiment' })}
                        </Button>
                    </StartExperimentDialog>
                </CardContent>
            </Card>);
    }
    const variantRows = results?.variants ?? active.variants.map((v) => ({ ...v, scans: 0, clicks: 0, clickThroughRate: 0 }));
    const totalScans = variantRows.reduce((sum, v) => sum + v.scans, 0);
    const totalClicks = variantRows.reduce((sum, v) => sum + v.clicks, 0);
    const overallCtr = totalScans > 0 ? (totalClicks / totalScans) * 100 : 0;
    const sortedByCtr = [...variantRows].sort((a, b) => b.clickThroughRate - a.clickThroughRate);
    const rankByKey = new Map(sortedByCtr.map((v, i) => [v.key, i]));
    const leaderKey = sortedByCtr[0]?.key;
    const peakCtr = Math.max(...variantRows.map((v) => v.clickThroughRate), 0);
    const insight = computeThemeExperimentInsight({ totalScans, variants: variantRows });
    return (<div className="flex flex-col gap-4">
            <div className="grid gap-4 lg:grid-cols-3">
                <HeroKpi className="lg:col-span-2" label={t('design_experiment.hero_label', { defaultValue: 'Theme experiment' })} value={totalScans.toLocaleString()} sublabel={totalScans > 0
            ? `${totalClicks.toLocaleString()} clicks · ${overallCtr.toFixed(1)}% overall CTR`
            : undefined}/>
                <InsightsPanel insights={insight ? [insight] : []} empty={t('design_experiment.insights_empty', {
            defaultValue: 'Live results refresh every 30s — give it a few more scans.',
        })}/>
            </div>

            <Card className="border-border/60">
                <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                            <CardTitle className="flex items-center gap-2 text-base">
                                <Beaker className="h-5 w-5"/>
                                {t('design_experiment.running_title', { defaultValue: 'Theme experiment running' })}
                                <Badge variant="secondary" className="capitalize">
                                    {active.status}
                                </Badge>
                            </CardTitle>
                            <CardDescription>
                                {t('design_experiment.running_description', {
            defaultValue: 'Live results — refreshing every 30s. Declare a winner to promote a variant onto the campaign and end the experiment.',
        })}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="pt-0">
                    <div className="overflow-x-auto rounded-md border border-border/60">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/40">
                                    <TableHead className="w-16">
                                        {t('design_experiment.variant_key', { defaultValue: 'Key' })}
                                    </TableHead>
                                    <TableHead>{t('design_experiment.theme', { defaultValue: 'Theme' })}</TableHead>
                                    <TableHead>{t('design_experiment.pattern', { defaultValue: 'Pattern' })}</TableHead>
                                    <TableHead className="text-right w-16">
                                        {t('design_experiment.weight', { defaultValue: 'Weight' })}
                                    </TableHead>
                                    <TableHead className="text-right w-20">
                                        {t('design_experiment.scans', { defaultValue: 'Scans' })}
                                    </TableHead>
                                    <TableHead className="text-right w-20">
                                        {t('design_experiment.clicks', { defaultValue: 'Clicks' })}
                                    </TableHead>
                                    <TableHead className="w-44">
                                        {t('design_experiment.ctr', { defaultValue: 'CTR' })}
                                    </TableHead>
                                    <TableHead className="w-40"/>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {variantRows.map((row) => {
            const ctrPct = row.clickThroughRate * 100;
            const ctrBar = peakCtr > 0 ? Math.max(2, (row.clickThroughRate / peakCtr) * 100) : 2;
            const isLeader = row.key === leaderKey;
            const tint = paletteFor(rankByKey.get(row.key) ?? 0);
            return (<TableRow key={row.key} className={isLeader ? 'bg-muted/30' : undefined}>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1.5 font-mono font-semibold">
                                                    {row.key}
                                                    {isLeader && totalScans > 0 ? (<Trophy className="h-3.5 w-3.5 text-amber-500" aria-label="Leader"/>) : null}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {row.pageDesign ?? (<span className="text-muted-foreground italic">inherit</span>)}
                                            </TableCell>
                                            <TableCell>
                                                {row.backgroundPattern ?? (<span className="text-muted-foreground italic">inherit</span>)}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">{row.weight}</TableCell>
                                            <TableCell className="text-right tabular-nums">{row.scans}</TableCell>
                                            <TableCell className="text-right tabular-nums">{row.clicks}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center justify-end gap-2">
                                                    <div className="relative h-2 w-24 overflow-hidden rounded-full bg-muted">
                                                        <div className={cn('absolute left-0 top-0 h-full rounded-full', tint)} style={{ width: `${ctrBar}%` }}/>
                                                    </div>
                                                    <span className="w-12 text-right tabular-nums">
                                                        {ctrPct.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button size="sm" variant={isLeader ? 'default' : 'outline'} disabled={concluding || row.scans === 0} onClick={() => handleConclude(row.key)}>
                                                    <Trophy className="h-4 w-4 mr-1"/>
                                                    {t('design_experiment.declare_winner', {
                    defaultValue: 'Declare winner',
                })}
                                                </Button>
                                            </TableCell>
                                        </TableRow>);
        })}
                            </TableBody>
                        </Table>
                    </div>
                    {resultsPending && (<p className="mt-3 text-xs text-muted-foreground">
                            {t('design_experiment.loading_results', { defaultValue: 'Loading live results…' })}
                        </p>)}
                </CardContent>
            </Card>
        </div>);
}
